import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api-client';
import { saveSession } from '../../lib/session';
import { useAuth } from '../../lib/auth';
import { ResonanceMark } from '../../components/ui/ResonanceMark';
import { PublicAppearanceSelector } from '../../components/ui/PublicAppearanceSelector';
import styles from './Public.module.css';

export function AuthCallbackPage() {
  const { completeOAuth, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const exchanged = useRef(false);
  const [change, setChange] = useState<{ code: string; currentEmail: string; newEmail: string } | null>(null);
  const [saving, setSaving] = useState(false);
  async function confirmChange() {
    if (!change || saving) return;
    setSaving(true);
    try {
      saveSession(await api.googleChangeComplete(change.code));
      window.location.replace('/app/settings?googleChanged=1');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Account change could not be completed.');
      setSaving(false);
    }
  }

  useEffect(() => {
    if (exchanged.current) return;
    exchanged.current = true;

    const params = new URLSearchParams(window.location.search);
    const changeCode = params.get('changeCode');
    const changeError = params.get('changeError');
    window.history.replaceState(null, '', window.location.pathname);
    if (changeError) { setError(changeError); return; }
    if (changeCode) {
      api.googleChangePreview(changeCode).then(preview => setChange({ code: changeCode, ...preview }))
        .catch((caught: unknown) => setError(caught instanceof Error ? caught.message : 'Unable to verify this change.'));
      return;
    }
    const code = params.get('code');
    if (!code) {
      setError('The sign-in link is missing its code.');
      return;
    }

    completeOAuth(code)
      .then(() => navigate({ to: '/app', replace: true }))
      .catch((caught: unknown) => {
        setError(caught instanceof Error ? caught.message : 'Sign-in could not be completed.');
      });
  }, [completeOAuth, navigate]);

  return (
    <div className={styles.screen}>
      <div className="appGradient" />
      <div className={styles.appearanceSelector}>
        <PublicAppearanceSelector />
      </div>
      <div className={styles.card}>
        <div className={styles.brand}>
          <ResonanceMark size={26} className={styles.mark} />
          Resonance
        </div>
        {error ? (
          <>
            <p className={styles.error}>{error}</p>
            <button type="button" className={styles.secondary} onClick={() => void navigate({ to: isAuthenticated ? '/app/settings' : '/sign-in' })}>
              {isAuthenticated ? 'Back to Settings' : 'Back to sign in'}
            </button>
          </>
        ) : change ? (
          <>
            <h1>Change Google account?</h1>
            <p>Switch from {change.currentEmail} to <strong>{change.newEmail}</strong>.</p>
            <p>Your workspace and all its data stay with you. Other devices will need to sign in again with the new Google account.</p>
            <button type="button" className={styles.secondary} disabled={saving} onClick={() => void confirmChange()}>{saving ? 'Changing account…' : 'Confirm change'}</button>
            <button type="button" className={styles.secondary} disabled={saving} onClick={() => window.location.replace('/app/settings')}>Cancel</button>
          </>
        ) : (
          <p className={styles.subtitle}>Signing you in…</p>
        )}
      </div>
    </div>
  );
}
